import React, { useState } from 'react';
import { Table, Alert } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import ChatComposer from './ChatComposer';
import './aiCrew.css';

/**
 * AnswerView - reply to a read-only question (an employee's schedule, who is on a crew).
 * Renders exactly what the API returned: title, subtitle, columns/rows, notes, and
 * "which one?" choices that re-ask the question with the person or crew spelled out.
 * Nothing here can change data.
 */
const initialsOf = (text) => String(text || '?').split(/[\s,_-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

export default function AnswerView({ answer, question, onAsk, maxLength = 500 }) {
  const [choiceError, setChoiceError] = useState(null);
  const askChoice = async (choice) => {
    setChoiceError(null);
    try { if (onAsk) await onAsk(choice.prompt, choice.selection ? { selection: choice.selection } : undefined); }
    catch { setChoiceError('That request could not be processed. Please try again.'); }
  };
  if (!answer) return null;

  const columns = (answer.columns || []).map((col) => ({
    title: col.label,
    dataIndex: col.key,
    key: col.key,
    render: (value) => value || '-',
  }));
  const rows = (answer.rows || []).map((row, index) => ({ key: index, ...row }));
  const choices = answer.choices || [];

  return (
    <div className="ai-crew-answer" data-testid="ai-crew-answer">
      {question && <div className="ai-crew-answer-question" data-testid="ai-crew-answer-question">{question}</div>}
      <div className="ai-crew-answer-head">
        <h2 className="ai-crew-answer-title">{answer.title}</h2>
        {answer.subtitle && <div className="ai-crew-answer-subtitle">{answer.subtitle}</div>}
      </div>

      {(answer.notes || []).map((note) => (
        <Alert key={note} type="warning" showIcon message={note} style={{ marginBottom: 12 }} />
      ))}

      {answer.message && <div className="ai-crew-answer-message">{answer.message}</div>}

      {choices.length > 0 && (
        <div className="ai-crew-choices" data-testid="ai-crew-answer-choices" role="list">
          {choices.map((choice) => (
            <button
              key={choice.prompt}
              type="button"
              role="listitem"
              className="ai-crew-choice"
              onClick={() => askChoice(choice)}
            >
              <span className="ai-crew-choice-mark" aria-hidden="true">{initialsOf(choice.title || choice.label)}</span>
              <span className="ai-crew-choice-text">
                <span className="ai-crew-choice-title">{choice.title || choice.label}</span>
                {choice.meta && <span className="ai-crew-choice-meta">{choice.meta}</span>}
              </span>
              <RightOutlined className="ai-crew-choice-go" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {columns.length > 0 && rows.length > 0 && (
        <Table
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={rows.length > 12 ? { pageSize: 12, size: 'small' } : false}
          scroll={{ x: 'max-content' }}
        />
      )}

      {choiceError && <p role="alert">{choiceError}</p>}
      <ChatComposer onAsk={onAsk} maxLength={maxLength} />
    </div>
  );
}
